import { ChangeDetectionStrategy, Component, Injector, Input, OnInit } from '@angular/core';
import { take } from 'rxjs/operators';
import { WidgetBaseComponent } from '../../widgetBase';

/**
 * GrossRentToNoi Widget
 */
@Component({
  selector: 'gross-rent-to-noi-widget',
  templateUrl: './gross-rent-to-noi-widget.component.html',
  styleUrls: ['../../common/css/general.scss', './gross-rent-to-noi-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GrossRentToNoiWidgetComponent extends WidgetBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Are charts ready to be displayed?
   */
  chartsReady = false;

  /**
   * Chart data
   */
  public chartData: any[] = [];

  /**
   * Title
   */
  public title = {
    text: 'Will be translated', font: 'bold 16px Arial'
  };

  /**
   * Chart categories
   */
  public categories = [];

  /**
   * Chart colors
   */
  public colors = {
    increase: '#D4D5D6',
    decrease: '#9B75A8',
    total: '#626468',
    hidden: 'transparent'
  }

  /**
   * Constructor
   * @param injector
   */
  constructor(injector: Injector) {
    super(injector, 'grossRentToNoi', true);
  }

  /**
   * Initializes the component
   */
  ngOnInit() {
    // will call registerEvents and onInitialized when config (this.widgetConfig and this.page) is ready
    this.initWidgetConfig();
  }

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useReadOnlySaveChangesHandler();
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    this.mockupData$ = this.widgetService.loadMockup('grossRentToNoi').pipe(take(1));

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.widgetService.load('grossRentToNoi', this.getReport()).pipe();
  }

  /**
   * Called by base class to let widget preprocess loaded data if required
   */
  protected preprocessLoadedData(data) {
    data.rows = [];

    this.reportDesignerTranslator.ready('grossRentToNoi').then(() => {
      this.title.text = this.reportDesignerTranslator.getString('grossRentToNoi.labelTitle');

      const fields = [
        { name: 'grossRent', category: this.reportDesignerTranslator.getString('grossRentToNoi.grossRent') },

        { name: 'reportValuationManagementCostsNonRecoverableAmount', category: this.reportDesignerTranslator.getString('grossRentToNoi.reportValuationManagementCostsNonRecoverableAmount'), invert: true },
        { name: 'reportValuationMaintenanceCostsNonRecoverableAmount', category: this.reportDesignerTranslator.getString('grossRentToNoi.reportValuationMaintenanceCostsNonRecoverableAmount'), invert: true },
        { name: 'reportValuationOtherCostsNonRecoverableAmount', category: this.reportDesignerTranslator.getString('grossRentToNoi.reportValuationOtherCostsNonRecoverableAmount'), invert: true },
        { name: 'reportValuationInsuranceCostsNonRecoverableAmount', category: this.reportDesignerTranslator.getString('grossRentToNoi.reportValuationInsuranceCostsNonRecoverableAmount'), invert: true },
        { name: 'reportValuationGroundTaxNonRecoverableAmount', category: this.reportDesignerTranslator.getString('grossRentToNoi.reportValuationGroundTaxNonRecoverableAmount'), invert: true },
        { name: 'reportValuationVacancyCostsNonRecoverableAmount', category: this.reportDesignerTranslator.getString('grossRentToNoi.reportValuationVacancyCostsNonRecoverableAmount'), invert: true },
        { name: 'reportValuationLeaseholdCostsNonRecoverableAmount', category: this.reportDesignerTranslator.getString('grossRentToNoi.reportValuationLeaseholdCostsNonRecoverableAmount'), invert: true },
        { name: 'reportValuationStructuralVacancyCostsNonRecoverableAmount', category: this.reportDesignerTranslator.getString('grossRentToNoi.reportValuationStructuralVacancyCostsNonRecoverableAmount'), invert: true },
        { name: 'reportValuationOtherSpecificCostsNonRecoverableAmount', category: this.reportDesignerTranslator.getString('grossRentToNoi.reportValuationOtherSpecificCostsNonRecoverableAmount'), invert: true },
        { name: 'reportValuationStatutoryExpensesNonRecoverableAmount', category: this.reportDesignerTranslator.getString('grossRentToNoi.reportValuationStatutoryExpensesNonRecoverableAmount'), invert: true },

        { name: 'noiBeforeRentFreeTotal', category: this.reportDesignerTranslator.getString('grossRentToNoi.noiBeforeRentFreeTotal'), totals: true, addOtherBefore: true },
        { name: 'rentFreePA', category: this.reportDesignerTranslator.getString('grossRentToNoi.rentFreePA'), invert: true },
        { name: 'noiIncludingRentFree', category: this.reportDesignerTranslator.getString('grossRentToNoi.noiIncludingRentFree'), totals: true }
      ];
      this.processFields(fields);

      this.chartsReady = true;
      this.ref.markForCheck();
    });

    return data;
  }

  /**
   * Process the fields
   * @param fields
   */
  private processFields(fields: any[]): void {
    this.chartData = [];
    let runningTotal = 0;

    if (fields?.length) {
      this.chartData.push({ category: fields[0].category, amount: this.widgetData[fields[0].name], color: this.colors.total });
      this.categories.push(fields[0].category);
    }

    for (let i = 1; i < fields.length; ++i) {
      const field = fields[i];
      let currentValue = this.widgetData[field.name];
      if (currentValue && field.invert) {
        currentValue *= (-1);
      }
      if (field.totals) {
        //Automatically insert an 'Other' entry that fixes the (running) total
        this.addOtherEntries(field, runningTotal, currentValue);
      } else {
        const color = currentValue > 0 ? this.colors.increase : this.colors.decrease;
        if (field.category || currentValue) {
          this.chartData.push({ category: field.category, amount: currentValue, color: color });
          this.categories.push(field.category);
        }
      }
      runningTotal += currentValue;
    }
  }

  /**
   * Add other entries
   * @param field
   * @param runningTotal
   * @param currentValue
   */
  private addOtherEntries(field: any, runningTotal: number, currentValue: any): void {
    if (field.addOtherBefore && runningTotal != currentValue) {
      this.chartData.push({ category: this.reportDesignerTranslator.getString('grossRentToNoi.labelOther'), amount: currentValue - runningTotal, color: this.colors.hidden, hidden: true });
      this.categories.push(this.reportDesignerTranslator.getString('grossRentToNoi.labelOther'));
    }

    this.chartData.push({ category: field.category, amount: currentValue, color: this.colors.total, summary: 'total' });
    this.categories.push(field.category);
  }

  /**
   * Checks if item is visible
   * @param e {any} Event object
   */
  public itemVisible(e: any) {
    return !e.dataItem.hidden;
  }

  /**
   * Returns item visual
   * @param e {any} Event object
   */
  public itemVisual(e: any) {
    if (e.dataItem.color === 'transparent') return;
    return e.createVisual();
  }

  /**
   * Legend item clicked event handler
   * @param e {any} Event object
   */
  public onLegendItemClick(e: any): void {
    /* Do not hide the series on legend click */
    e.preventDefault();
  }
}
